import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Chip,
  Grid,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import ActionButton from '../components/ActionButton';
import TableSkeleton from '../components/TableSkeleton';

const MotionBox = motion(Box);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`model-tabpanel-${index}`}
      aria-labelledby={`model-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function Models() {
  const [models, setModels] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, modelId: null as any });
  const [editingModel, setEditingModel] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectIdFilter, setProjectIdFilter] = useState('all');
  const [parentChildFilter, setParentChildFilter] = useState('all');
  const [modelTypeFilter, setModelTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [physicalAssetFilter, setPhysicalAssetFilter] = useState('all');
  const [iotEnabledFilter, setIotEnabledFilter] = useState('all');
  
  // Visualizations state
  const [visualizations, setVisualizations] = useState<any[]>([]);
  const [showVizForm, setShowVizForm] = useState(false);
  const [editingViz, setEditingViz] = useState<any>(null);
  const [vizFormData, setVizFormData] = useState({
    visualization_type: '',
    visualization_name: '',
    description: '',
    status: 1,
  });

  // Actions state
  const [actions, setActions] = useState<any[]>([]);
  const [showActionForm, setShowActionForm] = useState(false);
  const [editingAction, setEditingAction] = useState<any>(null);
  const [actionFormData, setActionFormData] = useState({
    action_name: '',
    action_type: '',
    status: 1,
  });

  const [formData, setFormData] = useState({
    project_id: null as any,
    parent_model_id: null as any,
    model_code: '',
    model_name: '',
    model_type: '',
    category: '',
    level: 0,
    original_model_id: '',
    source_system: '',
    is_physical_asset: true,
    is_iot_enabled: false,
    thumbnail_url: '',
    model_file_url: '',
    status: 1,
  });

  const fetchData = async (projectId?: string, parentChild?: string) => {
    try {
      setLoading(true);
      const params: any = {};
      if (projectId && projectId !== 'all') {
        params.project_id = projectId;
      }
      if (parentChild && parentChild !== 'all') {
        params.hierarchy_filter = parentChild === 'parent' ? 1 : 2;
      }
      const [modelsRes, projectsRes] = await Promise.all([
        API.models.list(params),
        API.projects.list(),
      ]);
      setModels(modelsRes?.data || []);
      setProjects(projectsRes?.data || []);
    } catch (err: any) {
      showError('Failed to load models');
      console.error('Models fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisualizations = async (modelId: number) => {
    try {
      const response = await API.models.visualizations.list(modelId);
      setVisualizations(response?.data || []);
    } catch (err: any) {
      showError('Failed to load visualizations');
      console.error('Visualizations fetch error:', err);
    }
  };

  const fetchActions = async (modelId: number) => {
    try {
      const response = await API.models.actions.list(modelId);
      setActions(response?.data || []);
    } catch (err: any) {
      showError('Failed to load actions');
      console.error('Actions fetch error:', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData(projectIdFilter, parentChildFilter);
  }, [projectIdFilter, parentChildFilter]);

  const handleOpenDialog = async (model: any = null) => {
    if (model) {
      setEditingModel(model);
      setFormData({
        project_id: model.project_id || '',
        parent_model_id: model.parent_model_id || '',
        model_code: model.model_code || '',
        model_name: model.model_name || '',
        model_type: model.model_type || '',
        category: model.category || '',
        level: model.level || 0,
        original_model_id: model.original_model_id || '',
        source_system: model.source_system || '',
        is_physical_asset: model.is_physical_asset !== false,
        is_iot_enabled: model.is_iot_enabled || false,
        thumbnail_url: model.thumbnail_url || '',
        model_file_url: model.model_file_url || '',
        status: model.status !== undefined ? model.status : 1,
      });
      
      // Fetch visualizations and actions for editing
      await Promise.all([
        fetchVisualizations(model.model_id),
        fetchActions(model.model_id),
      ]);
    } else {
      setEditingModel(null);
      setVisualizations([]);
      setActions([]);
      setFormData({
        project_id: null,
        parent_model_id: null,
        model_code: '',
        model_name: '',
        model_type: '',
        category: '',
        level: 0,
        original_model_id: '',
        source_system: '',
        is_physical_asset: true,
        is_iot_enabled: false,
        thumbnail_url: '',
        model_file_url: '',
        status: 1,
      });
    }
    setTabValue(0);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingModel(null);
    setVisualizations([]);
    setActions([]);
    setShowVizForm(false);
    setShowActionForm(false);
    setTabValue(0);
    setFormData({
      project_id: null,
      parent_model_id: null,
      model_code: '',
      model_name: '',
      model_type: '',
      category: '',
      level: 0,
      original_model_id: '',
      source_system: '',
      is_physical_asset: true,
      is_iot_enabled: false,
      thumbnail_url: '',
      model_file_url: '',
      status: 1,
    });
  };

  const handleSubmit = async () => {
    try {
      // Prepare data with proper types
      const submitData: any = {
        ...formData,
        project_id: formData.project_id ? Number(formData.project_id) : undefined,
        parent_model_id: formData.parent_model_id ? Number(formData.parent_model_id) : undefined,
      };

      // Remove empty/null values for optional fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === null || submitData[key] === undefined) {
          delete submitData[key];
        }
      });

      if (editingModel) {
        await API.models.update(editingModel.model_id, submitData);
        showSuccess('Model updated successfully!');
        handleCloseDialog();
        fetchData();
      } else {
        const response = await API.models.create(submitData);
        showSuccess('Model created successfully! You can now add visualizations and actions.');
        
        // Get the created model data and set it as editingModel
        const createdModel = response?.data?.[0] || response?.data;
        if (createdModel && createdModel.model_id) {
          setEditingModel(createdModel);
          // Switch to visualizations tab to encourage adding them
          setTabValue(1);
        } else {
          // If we can't get the model ID, close and refresh
          handleCloseDialog();
        }
        fetchData();
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
        (editingModel ? 'Failed to update model' : 'Failed to create model');
      showError(errorMessage);
      console.error('Model submit error:', err);
    }
  };

  const handleDeleteClick = (modelId: number) => {
    setConfirmDialog({ open: true, modelId });
  };

  const handleDelete = async () => {
    try {
      await API.models.deactivate(confirmDialog.modelId);
      showSuccess('Model deleted successfully!');
      fetchData();
    } catch (err: any) {
      showError('Failed to delete model');
      console.error('Model delete error:', err);
    } finally {
      setConfirmDialog({ open: false, modelId: null });
    }
  };

  // Visualization handlers
  const handleOpenVizForm = (viz: any = null) => {
    if (viz) {
      setEditingViz(viz);
      setVizFormData({
        visualization_type: viz.visualization_type || '',
        visualization_name: viz.visualization_name || '',
        description: viz.description || '',
        status: viz.status !== undefined ? viz.status : 1,
      });
    } else {
      setEditingViz(null);
      setVizFormData({
        visualization_type: '',
        visualization_name: '',
        description: '',
        status: 1,
      });
    }
    setShowVizForm(true);
  };

  const handleCloseVizForm = () => {
    setShowVizForm(false);
    setEditingViz(null);
    setVizFormData({
      visualization_type: '',
      visualization_name: '',
      description: '',
      status: 1,
    });
  };

  const handleSubmitVisualization = async () => {
    if (!editingModel) {
      showError('Please save the model first before adding visualizations');
      return;
    }

    // Validate required fields
    if (!vizFormData.visualization_type || !vizFormData.visualization_name) {
      showError('Visualization Type and Name are required');
      return;
    }

    try {
      console.log('Submitting visualization:', vizFormData, 'for model:', editingModel.model_id);
      
      if (editingViz) {
        await API.models.visualizations.update(
          editingModel.model_id,
          editingViz.visualization_id,
          vizFormData
        );
        showSuccess('Visualization updated successfully!');
      } else {
        const response = await API.models.visualizations.create(editingModel.model_id, vizFormData);
        console.log('Visualization created:', response);
        showSuccess('Visualization created successfully!');
      }
      handleCloseVizForm();
      fetchVisualizations(editingModel.model_id);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
        (editingViz ? 'Failed to update visualization' : 'Failed to create visualization');
      showError(errorMessage);
      console.error('Visualization submit error:', err);
    }
  };

  const handleDeleteVisualization = async (vizId: number) => {
    if (!editingModel) return;
    
    try {
      await API.models.visualizations.delete(editingModel.model_id, vizId);
      showSuccess('Visualization deleted successfully!');
      fetchVisualizations(editingModel.model_id);
    } catch (err: any) {
      showError('Failed to delete visualization');
      console.error('Visualization delete error:', err);
    }
  };

  const handleToggleVisualizationStatus = async (viz: any) => {
    if (!editingModel) return;
    
    try {
      const newStatus = viz.status === 1 ? 0 : 1;
      await API.models.visualizations.update(
        editingModel.model_id,
        viz.visualization_id,
        { status: newStatus }
      );
      showSuccess(`Visualization ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
      fetchVisualizations(editingModel.model_id);
    } catch (err: any) {
      showError('Failed to update visualization status');
      console.error('Visualization status update error:', err);
    }
  };

  // Action handlers
  const handleOpenActionForm = (action: any = null) => {
    if (action) {
      setEditingAction(action);
      setActionFormData({
        action_name: action.action_name || '',
        action_type: action.action_type || '',
        status: action.status !== undefined ? action.status : 1,
      });
    } else {
      setEditingAction(null);
      setActionFormData({
        action_name: '',
        action_type: '',
        status: 1,
      });
    }
    setShowActionForm(true);
  };

  const handleCloseActionForm = () => {
    setShowActionForm(false);
    setEditingAction(null);
    setActionFormData({
      action_name: '',
      action_type: '',
      status: 1,
    });
  };

  const handleSubmitAction = async () => {
    if (!editingModel) {
      showError('Please save the model first before adding actions');
      return;
    }

    // Validate required fields
    if (!actionFormData.action_name || !actionFormData.action_type) {
      showError('Action Name and Type are required');
      return;
    }

    try {
      console.log('Submitting action:', actionFormData, 'for model:', editingModel.model_id);
      
      if (editingAction) {
        await API.models.actions.update(
          editingModel.model_id,
          editingAction.action_id,
          actionFormData
        );
        showSuccess('Action updated successfully!');
      } else {
        const response = await API.models.actions.create(editingModel.model_id, actionFormData);
        console.log('Action created:', response);
        showSuccess('Action created successfully!');
      }
      handleCloseActionForm();
      fetchActions(editingModel.model_id);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 
        (editingAction ? 'Failed to update action' : 'Failed to create action');
      showError(errorMessage);
      console.error('Action submit error:', err);
    }
  };

  const handleDeleteAction = async (actionId: number) => {
    if (!editingModel) return;
    
    try {
      await API.models.actions.delete(editingModel.model_id, actionId);
      showSuccess('Action deleted successfully!');
      fetchActions(editingModel.model_id);
    } catch (err: any) {
      showError('Failed to delete action');
      console.error('Action delete error:', err);
    }
  };

  const handleToggleActionStatus = async (action: any) => {
    if (!editingModel) return;
    
    try {
      const newStatus = action.status === 1 ? 0 : 1;
      await API.models.actions.update(
        editingModel.model_id,
        action.action_id,
        { status: newStatus }
      );
      showSuccess(`Action ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
      fetchActions(editingModel.model_id);
    } catch (err: any) {
      showError('Failed to update action status');
      console.error('Action status update error:', err);
    }
  };

  const getProjectName = (projectId: number) => {
    const project = projects.find((p: any) => p.project_id === projectId);
    return project ? project.project_name : 'Unknown Project';
  };

  const getStatusLabel = (status: number) => {
    switch (status) {
      case 1: return 'Active';
      case 2: return 'Inactive';
      case 0: return 'Deleted';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: return { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' }; // Active - Green
      case 2: return { bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' }; // Inactive - Yellow
      case 0: return { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' }; // Deleted - Red
      default: return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' }; // Default - Gray
    }
  };

  const filteredModels = models.filter((model: any) => {
    const matchesSearch = model.model_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.model_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getProjectName(model.project_id)?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Project filtering is now done via API, so we don't need client-side project filter
    const matchesModelType = modelTypeFilter === 'all' || model.model_type === modelTypeFilter;
    const matchesCategory = categoryFilter === 'all' || model.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || model.status === parseInt(statusFilter);
    const matchesPhysicalAsset = physicalAssetFilter === 'all' || 
      (physicalAssetFilter === 'physical' && model.is_physical_asset) ||
      (physicalAssetFilter === 'non-physical' && !model.is_physical_asset);
    const matchesIotEnabled = iotEnabledFilter === 'all' || 
      (iotEnabledFilter === 'enabled' && model.is_iot_enabled) ||
      (iotEnabledFilter === 'disabled' && !model.is_iot_enabled);
    
    return matchesSearch && matchesModelType && matchesCategory && matchesStatus && matchesPhysicalAsset && matchesIotEnabled;
  });

  const uniqueModelTypes = [...new Set(models.map((m: any) => m.model_type).filter(Boolean))];
  const uniqueCategories = [...new Set(models.map((m: any) => m.category).filter(Boolean))];

  const columns = [
    {
      field: 'model_name',
      headerName: 'Model Name',
      width: '18%',
      render: (row: any) => (
        <Typography variant="body2" fontWeight={600}>
          {row.model_name}
        </Typography>
      ),
    },
    {
      field: 'model_code',
      headerName: 'Model Code',
      width: '10%',
      render: (row: any) => (
        <Chip 
          label={row.model_code} 
          size="small" 
          sx={{ 
            fontWeight: 600,
            backgroundColor: '#EEF2FF',
            color: '#4F46E5',
            border: '1px solid #C7D2FE',
            fontSize: '0.7rem',
          }}
        />
      ),
    },
    {
      field: 'model_type',
      headerName: 'Type',
      width: '12%',
      render: (row: any) => row.model_type || '-',
    },
    {
      field: 'category',
      headerName: 'Category',
      width: '10%',
      render: (row: any) => row.category || '-',
    },
    {
      field: 'level',
      headerName: 'Level',
      width: '8%',
      render: (row: any) => row.level || 0,
    },
    {
      field: 'is_physical_asset',
      headerName: 'Physical',
      width: '10%',
      render: (row: any) => (
        <Chip
          label={row.is_physical_asset ? 'Yes' : 'No'}
          size="small"
          sx={{ 
            fontWeight: 600,
            fontSize: '0.7rem',
            backgroundColor: row.is_physical_asset ? '#DBEAFE' : '#F3F4F6',
            color: row.is_physical_asset ? '#1E40AF' : '#6B7280',
            border: `1px solid ${row.is_physical_asset ? '#93C5FD' : '#D1D5DB'}`,
          }}
        />
      ),
    },
    {
      field: 'is_iot_enabled',
      headerName: 'Connected',
      width: '10%',
      render: (row: any) => (
        <Chip
          label={row.is_iot_enabled ? 'Yes' : 'No'}
          size="small"
          sx={{ 
            fontWeight: 600,
            fontSize: '0.7rem',
            backgroundColor: row.is_iot_enabled ? '#D1FAE5' : '#F3F4F6',
            color: row.is_iot_enabled ? '#065F46' : '#6B7280',
            border: `1px solid ${row.is_iot_enabled ? '#6EE7B7' : '#D1D5DB'}`,
          }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: '10%',
      render: (row: any) => {
        const colors = getStatusColor(row.status);
        return (
          <Chip
            label={getStatusLabel(row.status)}
            size="small"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.7rem',
              backgroundColor: colors.bg,
              color: colors.color,
              border: `1px solid ${colors.border}`,
            }}
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: '12%',
      render: (row: any) => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-',
    },
  ];

  const filterFields = [
    {
      label: 'Project',
      value: projectIdFilter,
      onChange: setProjectIdFilter,
      options: [
        { value: 'all', label: 'All Projects' },
        ...projects.map((project: any) => ({ 
          value: String(project.project_id), 
          label: project.project_name || `Project ${project.project_id}` 
        })),
      ],
    },
    {
      label: 'Model Level',
      value: parentChildFilter,
      onChange: setParentChildFilter,
      options: [
        { value: 'all', label: 'All' },
        { value: 'parent', label: 'Parent' },
        { value: 'child', label: 'Child' },
      ],
    },
    {
      label: 'Model Type',
      value: modelTypeFilter,
      onChange: setModelTypeFilter,
      options: [
        { value: 'all', label: 'All Types' },
        ...uniqueModelTypes.map((type: any) => ({ value: type, label: type })),
      ],
    },
    {
      label: 'Category',
      value: categoryFilter,
      onChange: setCategoryFilter,
      options: [
        { value: 'all', label: 'All Categories' },
        ...uniqueCategories.map((cat: any) => ({ value: cat, label: cat })),
      ],
    },
    {
      label: 'Physical Asset',
      value: physicalAssetFilter,
      onChange: setPhysicalAssetFilter,
      options: [
        { value: 'all', label: 'All' },
        { value: 'physical', label: 'Physical' },
        { value: 'non-physical', label: 'Non-Physical' },
      ],
    },
    {
      label: 'Connected',
      value: iotEnabledFilter,
      onChange: setIotEnabledFilter,
      options: [
        { value: 'all', label: 'All' },
        { value: 'enabled', label: 'Connected' },
        { value: 'disabled', label: 'Not Connected' },
      ],
    },
    {
      label: 'Status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: '1', label: 'Active' },
        { value: '2', label: 'Inactive' },
        { value: '0', label: 'Deleted' },
      ],
    },
  ];

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            Models
          </Typography>
        </Box>
        <TableSkeleton columns={7} rows={6} />
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Models
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage digital twin models and configurations
          </Typography>
        </Box>
        <ActionButton
          startIcon={<AddIcon />}
          endIcon={null}
          onClick={() => handleOpenDialog()}
        >
          Add Model
        </ActionButton>
      </Box>

      <FilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search models..."
        filters={filterFields}
      />

      <DataTable
        columns={columns}
        data={filteredModels}
        onEdit={handleOpenDialog}
        onDelete={(row: any) => handleDeleteClick(row.model_id)}
        onView={null}
        getRowId={(row: any) => row.model_id}
        emptyMessage="No models found"
      />

      {/* Large Modal with Tabs */}
      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            minHeight: '80vh',
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}>
          <Typography variant="h6" fontWeight={700}>
            {editingModel ? 'Edit Model' : 'Add New Model'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
        >
          <Tab label="Model Details" />
          <Tab label="Visualizations" disabled={!editingModel} />
          <Tab label="Actions" disabled={!editingModel} />
        </Tabs>

        <DialogContent sx={{ p: 0 }}>
          {/* Tab 1: Model Details */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Model Name"
                    value={formData.model_name}
                    onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                    required
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Model Code"
                    value={formData.model_code}
                    onChange={(e) => setFormData({ ...formData, model_code: e.target.value })}
                    required
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Project"
                    value={formData.project_id || ''}
                    onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    required
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  >
                    {projects.map((project: any) => (
                      <MenuItem key={project.project_id} value={project.project_id}>
                        {project.project_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Parent Model (Optional)"
                    value={formData.parent_model_id || ''}
                    onChange={(e) => setFormData({ ...formData, parent_model_id: e.target.value })}
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {models.filter((m: any) => m.model_id !== editingModel?.model_id).map((model: any) => (
                      <MenuItem key={model.model_id} value={model.model_id}>
                        {model.model_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Model Type"
                    value={formData.model_type}
                    onChange={(e) => setFormData({ ...formData, model_type: e.target.value })}
                    required
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Level"
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) || 0 })}
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Physical Asset"
                    value={formData.is_physical_asset}
                    onChange={(e) => setFormData({ ...formData, is_physical_asset: e.target.value === 'true' })}
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="IoT Enabled"
                    value={formData.is_iot_enabled}
                    onChange={(e) => setFormData({ ...formData, is_iot_enabled: e.target.value === 'true' })}
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Source System"
                    value={formData.source_system}
                    onChange={(e) => setFormData({ ...formData, source_system: e.target.value })}
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Thumbnail URL"
                    value={formData.thumbnail_url}
                    onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                    type="url"
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                    sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                  >
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={2}>Inactive</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          {/* Tab 2: Visualizations */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                  Model Visualizations
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => handleOpenVizForm()}
                  size="small"
                >
                  Add Visualization
                </Button>
              </Box>

              {showVizForm && (
                <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>
                    {editingViz ? 'Edit Visualization' : 'New Visualization'}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Visualization Type"
                        value={vizFormData.visualization_type}
                        onChange={(e) => setVizFormData({ ...vizFormData, visualization_type: e.target.value })}
                        required
                        size="small"
                        placeholder="e.g., 3D Model, Chart, Dashboard"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Visualization Name"
                        value={vizFormData.visualization_name}
                        onChange={(e) => setVizFormData({ ...vizFormData, visualization_name: e.target.value })}
                        required
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={vizFormData.description}
                        onChange={(e) => setVizFormData({ ...vizFormData, description: e.target.value })}
                        multiline
                        rows={2}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="Status"
                        value={vizFormData.status}
                        onChange={(e) => setVizFormData({ ...vizFormData, status: parseInt(e.target.value) })}
                        size="small"
                      >
                        <MenuItem value={1}>Active</MenuItem>
                        <MenuItem value={0}>Inactive</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Button size="small" onClick={handleCloseVizForm}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={handleSubmitVisualization}>
                          {editingViz ? 'Update' : 'Add'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visualizations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No visualizations added yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visualizations.map((viz: any) => (
                        <TableRow key={viz.visualization_id}>
                          <TableCell>{viz.visualization_type}</TableCell>
                          <TableCell>{viz.visualization_name}</TableCell>
                          <TableCell>{viz.description || '-'}</TableCell>
                          <TableCell>
                            <Switch
                              checked={viz.status === 1}
                              onChange={() => handleToggleVisualizationStatus(viz)}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleOpenVizForm(viz)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteVisualization(viz.visualization_id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Tab 3: Actions */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ px: 3, pb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                  Model Actions
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => handleOpenActionForm()}
                  size="small"
                >
                  Add Action
                </Button>
              </Box>

              {showActionForm && (
                <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>
                    {editingAction ? 'Edit Action' : 'New Action'}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Action Name"
                        value={actionFormData.action_name}
                        onChange={(e) => setActionFormData({ ...actionFormData, action_name: e.target.value })}
                        required
                        size="small"
                        placeholder="e.g., Start, Stop, Reset"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Action Type"
                        value={actionFormData.action_type}
                        onChange={(e) => setActionFormData({ ...actionFormData, action_type: e.target.value })}
                        required
                        size="small"
                        placeholder="e.g., Command, Query, Event"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="Status"
                        value={actionFormData.status}
                        onChange={(e) => setActionFormData({ ...actionFormData, status: parseInt(e.target.value) })}
                        size="small"
                      >
                        <MenuItem value={1}>Active</MenuItem>
                        <MenuItem value={0}>Inactive</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Button size="small" onClick={handleCloseActionForm}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={handleSubmitAction}>
                          {editingAction ? 'Update' : 'Add'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Action Name</strong></TableCell>
                      <TableCell><strong>Action Type</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No actions added yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      actions.map((action: any) => (
                        <TableRow key={action.action_id}>
                          <TableCell>{action.action_name}</TableCell>
                          <TableCell>{action.action_type}</TableCell>
                          <TableCell>
                            <Switch
                              checked={action.status === 1}
                              onChange={() => handleToggleActionStatus(action)}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleOpenActionForm(action)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteAction(action.action_id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>
        </DialogContent>

        <Divider />
        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', bgcolor: 'grey.50' }}>
          <ActionButton variant="outlined" startIcon={null} endIcon={null} onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          {tabValue === 0 && (
            <ActionButton variant="contained" startIcon={null} endIcon={null} onClick={handleSubmit}>
              {editingModel ? 'Update Model' : 'Create Model'}
            </ActionButton>
          )}
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, modelId: null })}
        onConfirm={handleDelete}
        title="Delete Model"
        message="Are you sure you want to delete this model? This action cannot be undone."
        confirmText="Delete"
        type="danger"
      />
    </MotionBox>
  );
}

export default Models;
